import go from 'gojs';

go.Diagram.licenseKey = '298647e0b36648c702d90676423d6bf919a175639c8418a30e0413f6e9083c06329fea2859d3829787ac4eff1f7d90d0dcc03e20c349023fe531dbdb11e18ea9bb3373e5140a4f88a45626969eff2bf1ae6a61f497e571a288288de0fba8c5cc08e0a3';

/**
 * Generates a Go.js process diagram as SVG from processMiner diagram data
 * @param {Object} diagramData - Diagram data with nodeDataArray and linkDataArray from processMiner
 * @param {number} width - Diagram width in pixels
 * @param {number} height - Diagram height in pixels
 * @returns {string} SVG string
 */
export function generateProcessDiagramSVG(diagramData) {
  const $ = go.GraphObject.make;
  
  if (!diagramData || !diagramData.nodeDataArray || !diagramData.linkDataArray) {
    return '<svg width="800" height="200"><text x="400" y="100" text-anchor="middle" fill="#6B778C">No diagram data available</text></svg>';
  }
  
  // Create temporary div for Go.js
  const tempDiv = document.createElement('div');
  tempDiv.style.width = '1200px';
  tempDiv.style.height = '800px';
  tempDiv.style.position = 'absolute';
  tempDiv.style.top = '-10000px';
  document.body.appendChild(tempDiv);
  
  try {
    // EXACT visualizer settings
    const diagram = $(go.Diagram, tempDiv, {
      defaultScale: 2,
      initialAutoScale: go.Diagram.Uniform,
      layout: $(go.LayeredDigraphLayout, { direction: 90 }),
      scrollMode: go.Diagram.InfiniteScroll,
      contentAlignment: go.Spot.Center,
      'undoManager.isEnabled': true
    });
  
  diagram.toolManager.mouseWheelBehavior = go.ToolManager.WheelZoom;
  diagram.toolManager.hoverDelay = 300;
  diagram.toolManager.toolTipDuration = 10000;
  
  // Use processMiner's exact node template
  diagram.nodeTemplate = $(
    go.Node,
    'Auto',
    { locationSpot: go.Spot.Center },
    $(
      go.Shape,
      new go.Binding('figure', 'figure'),
      { stroke: 'lightgray' },
      new go.Binding('fill', 'fill_color')
    ),
    $(
      go.TextBlock,
      new go.Binding('font', 'font'),
      {
        textAlign: 'center',
        margin: 3,
      },
      new go.Binding('text'),
      new go.Binding('stroke', 'text_color'),
      new go.Binding('visible', 'visible')
    )
  );
  
  // Use processMiner's exact link template
  diagram.linkTemplate = $(
    go.Link,
    { curve: go.Link.Bezier },
    $(
      go.Shape,
      new go.Binding('stroke', 'link_color'),
      new go.Binding('strokeWidth', 'width'),
      new go.Binding('visible', 'visible'),
      new go.Binding('strokeDashArray', 'dashes', function (l) {
        return l ? [3, 2] : null;
      })
    ),
    $(go.Shape, { toArrow: 'standard', strokeWidth: 0 }, new go.Binding('fill', 'link_color')),
    $(
      go.Panel,
      'Auto',
      $(
        go.Shape,
        {
          fill: $(go.Brush, 'Radial', { 0: 'rgb(240, 240, 240)', 0.3: 'rgb(240, 240, 240)', 1: 'rgba(240, 240, 240, 0)' }),
          stroke: null,
        }
      ),
      $(
        go.TextBlock,
        {
          textAlign: 'center',
          margin: 8,
        },
        new go.Binding('font', 'font'),
        new go.Binding('visible', 'label_visible'),
        new go.Binding('text', 'link_label')
      )
    )
  );
  
  // Extract data from diagramData
  const nodeDataArray = diagramData.nodeDataArray;
  const linkDataArray = diagramData.linkDataArray;
  
  if (!nodeDataArray || nodeDataArray.length === 0) {
    return '<svg width="800" height="200"><text x="400" y="100" text-anchor="middle" fill="#6B778C">No nodes in diagram</text></svg>';
  }
  
  // Set the model
  diagram.model = new go.GraphLinksModel(nodeDataArray, linkDataArray);
  
  // Force layout
  try {
    diagram.layout.doLayout(diagram);
  } catch (error) {
    console.error('Layout error:', error);
  }
  
  // Generate SVG - make it large enough to fit everything
  let svg;
  try {
    svg = diagram.makeSvg({
      scale: 2,  // Match defaultScale
      maxSize: new go.Size(Infinity, Infinity),  // No size limit
      background: "#FAFBFC"
    });
  } catch (error) {
    console.error('SVG generation error:', error);
    return '<svg width="800" height="200"><text x="400" y="100" text-anchor="middle" fill="#FF0000">Error generating diagram</text></svg>';
  }
  
  if (!svg || !(svg instanceof SVGElement)) {
    return '<svg width="800" height="200"><text x="400" y="100" text-anchor="middle" fill="#FF0000">Invalid SVG returned</text></svg>';
  }
  
  const svgString = new XMLSerializer().serializeToString(svg);
  return svgString;
  
  } finally {
    // Clean up temp div
    if (tempDiv && tempDiv.parentNode) {
      document.body.removeChild(tempDiv);
    }
  }
}

/**
 * Builds node and link data from step and variant analytics
 */
function buildDiagramData(stepData, variantData) {
  // Create nodes from steps
  const nodeMap = new Map();
  const nodeDataArray = [];
  
  if (!stepData || stepData.length === 0) {
    console.warn('No step data provided');
    return { nodeDataArray: [], linkDataArray: [] };
  }
  
  stepData.forEach(step => {
    // Step might use abbreviated property names like 'n' for name
    const stepName = step.n || step.step || step.name || step.activity || '';
    
    if (!stepName) {
      console.warn('Step missing name:', step);
      return;
    }
    
    if (!nodeMap.has(stepName)) {
      nodeMap.set(stepName, true);
      nodeDataArray.push({
        key: stepName,
        name: stepName,
        count: step.v || step.count || 0,
        avgDuration: step.avgDuration || step.avg_duration || 0
      });
    }
  });
  
  // Build links from variants
  const linkMap = new Map();
  
  if (!variantData || variantData.length === 0) {
    console.warn('No variant data provided to build links');
    return { nodeDataArray, linkDataArray: [] };
  }
  
  variantData.forEach((variant, idx) => {
    // Variant uses 'n' property for the path/name
    const path = variant.n || variant.path || variant.variant || variant.variant_path || '';
    
    if (!path) {
      console.warn('Variant missing path (n property):', variant);
      return;
    }
    
    // Split by arrow separator (might be different formats)
    const steps = path.split(/\s*→\s*|\s*->\s*|\s*→\s*/);
    
    if (steps.length < 2) {
      console.warn('Variant path has fewer than 2 steps:', path);
      return;
    }
    
    for (let i = 0; i < steps.length - 1; i++) {
      const from = steps[i].trim();
      const to = steps[i + 1].trim();
      
      if (!from || !to) continue;
      
      const key = `${from}→${to}`;
      
      if (!linkMap.has(key)) {
        linkMap.set(key, { from, to, count: 0 });
      }
      // Use v property for count, or default to 1
      linkMap.get(key).count += (variant.v || variant.count || 1);
    }
  });
  
  const linkDataArray = Array.from(linkMap.values());
  
  return { nodeDataArray, linkDataArray };
}
